import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  effect,
  EffectRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  untracked,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { formatISO, isDate, parseISO, setISOWeek } from 'date-fns';
import { domToCanvas } from 'modern-screenshot';
import { catchError, map, Observable, Observer, of, shareReplay, Subject, Subscription, takeUntil } from 'rxjs';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { hasRichTitle } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { BY_PROJECT_WIDGET_TYPES, ForecastMeta, PreviousMeta, PROJECT_ROWS_WIDGET_TYPES, TenantAggregateFailure } from 'src/app/interfaces/api/chart';
import { WidgetV2 } from 'src/app/interfaces/shared/widget';
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { AuthService } from 'src/app/services/core/auth.service';

// The loading key of a query for all the projects at once (tenant, or no project list).
const ALL_PROJECTS = '__all__';

@Directive()
export abstract class WidgetDirective implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // Services
  protected readonly chartService: ChartService = inject(ChartService);
  private readonly auth: AuthService = inject(AuthService);
  protected galleryTool: GalleryTool = inject(GalleryTool);
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected route: ActivatedRoute = inject(ActivatedRoute);

  // Component API
  @ViewChild('htmlContent', { static: false }) htmlContent!: ElementRef;
  @Input() widget!: WidgetV2;
  @Input() filters: any;
  @Input() project: any;
  @Input() visible: boolean = true;
  @Input() embed: boolean = false;
  @Input() width: number = 300;
  @Input() height: number = 200;
  @Input() projects: any[] = [];
  @Input() resize: boolean = false;
  @Input() refreshSignal?: Signal<number>;
  @Input() signalSingled?: Signal<string>;
  @Input() visualSignal?: Signal<number>;
  // Synthesis: one query merged over the tenant projects by the API, instead of one per project.
  @Input() tenantId?: string;
  // Synthesis: the tenant projects removed from the page, read at each query.
  @Input() excludeProjects?: Signal<string[]>;
  @Output() onExport: EventEmitter<any> = new EventEmitter();
  @Output() query: EventEmitter<any> = new EventEmitter();

  // Variables
  id: string | undefined = "";
  key: string | undefined = "";
  // Read from the widget, not copied at init: an edited title shows at once, with no query.
  get title(): string | undefined {
    return this.widget?.title;
  }
  type: string | undefined = "block";
  showTitle: boolean = true;
  widgetData: any;
  widgetDesign: any;
  widgetQuery: any;
  dataByProject: any = {};
  loading: any[] = [];
  private readonly queries = new Map<any, Subscription>();   // the query in flight per project
  error?: string;
  destroy$ = new Subject<void>();
  widgetRendered: boolean = false;
  failedSources: TenantAggregateFailure[] = [];
  forecastMeta: ForecastMeta | null = null;
  previousMeta: PreviousMeta | null = null;
  protected destroyEffect?: EffectRef;

  constructor() {
    this.initQueryParams();
    this.initSignalEffects();
  }

  // Initialisation des query params
  private initQueryParams(): void {
    const queryParamsSignal = toSignal(this.route.queryParamMap);
    const params = queryParamsSignal();

    if (params) {
      const dashboardId = params.get('dashboardId');
      this.id = params.get('chart') || '';
      this.key = params.get('key') || '';
      this.project = params.get('project') || '';
      const startDate = params.get('startDate');
      const endDate = params.get('endDate');

      this.getEmbedChart();
    }
  }

  // Initialisation des effets pour les signaux
  private initSignalEffects(): void {
    let lastRefreshValue: number | undefined = undefined;
    let lastExportSingledValue: string | undefined = undefined;
    let lastVisualValue: number | undefined = undefined;

    this.destroyEffect = effect(() => {
      // Signal global : refresh
      if (this.refreshSignal) {
        const value = this.refreshSignal();
        if (lastRefreshValue !== undefined && value !== lastRefreshValue) {
          this.updateData();
        }

        lastRefreshValue = value;
      }

      // Signal individuel : export jpeg/pdf & refresh manuel
      if (this.signalSingled) {
        const value = this.signalSingled();

        if (lastExportSingledValue !== undefined && value !== lastExportSingledValue) {
          if (value.startsWith('download')) this.export();
          else if (value.startsWith('canvas')) this.getCanvas();
          else if (value.startsWith('refresh_manuel')) {
            this.updateData();
          }
        }

        lastExportSingledValue = value;
      }

      // Signal visuel : rafraîchissement visuel
      if (this.visualSignal) {
        const value = this.visualSignal();

        if (lastVisualValue !== undefined && value !== lastVisualValue) {
          this.init();
        }

        lastVisualValue = value;
      }
    }
    );
  };

  ngOnInit(): void {
    this.updateData();
  }

  ngAfterViewInit(): void {
    if (this.embed) {
      this.updateData();
    }
  }

  ngOnChanges(): void {
    if (this.widget && !this.resize) {
      this.id = this.widget.id;
      this.width = this.widget.width ?? 300;
      this.height = this.widget.height ?? 200;
      this.widgetDesign = this.widget.design;
      this.widgetData = this.widget.data;
      this.widgetQuery = this.widget.query;

      if (this.widgetDesign && !this.widgetDesign.datasetNames) {
        this.widgetDesign.datasetNames = [];
      }

      this.type = this.widget.type;
      this.init();
    }
  }

  ngOnDestroy(): void {
    if (this.destroyEffect) {
      this.destroyEffect.destroy();
    }

    this.widgetDesign = null;
    this.widgetData = null;
    this.dataByProject = {};
    this.visible = false;

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Récupération du widget en mode embed
  getEmbedChart(): void {
    if (this.id && this.project && this.key) {
      this.auth.setProjectKey(this.project, this.key);
      this.chartService
        .getChart(this.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (widget) => {
            this.widget = widget;
            this.sendQuery();
          },
          error: (err) => {
            console.error(err);
          }
        });
    }
  }

  // Mise à jour des données
  updateData(): void {
    // Embed: not loaded yet, getEmbedChart queries once it is.
    if (!this.widget) return;

    this.widget.data = [];
    this.forecastMeta = null;

    if (!this.tenantId && this.widget.projects?.length) {
      this.widget.projects.forEach((p) => {
        this.sendQuery(p);
      });
    } else {
      this.sendQuery();
    }
  }

  // Vérification du chargement
  isLoading(): boolean {
    return this.loading.length > 0;
  }

  // A query in flight per project, or one for all of them (no project: the tenant, or a widget without projects):
  // the spinner shows until the last answer.
  protected startLoading(project: any): void {
    const key = project ?? ALL_PROJECTS;
    if (!this.loading.includes(key)) this.loading.push(key);
  }

  // A newer query for the same project replaces the one in flight: its stale answer is never shown.
  protected runQuery(project: any, rows$: Observable<any[]>, observer: Partial<Observer<any[]>>): void {
    const key = project ?? ALL_PROJECTS;
    this.queries.get(key)?.unsubscribe();
    this.queries.set(key, rows$.pipe(takeUntil(this.destroy$)).subscribe(observer));
  }

  // Its answer handled: the widget asks to be checked again. Under an OnPush page (the synthesis), nothing else
  // would show the data, until a mouse move or another signal.
  protected stopLoading(project: any): void {
    const index = this.loading.indexOf(project ?? ALL_PROJECTS);
    if (index >= 0) this.loading.splice(index, 1);
    this.cdr.markForCheck();
  }

  // Envoi de la requête
  sendQuery(project: any = null): boolean {
    if (!this.widget.query) return false;

    if (!this.validationGraphOption()) {
      this.init();
      return false;
    }

    this.widgetData = null;
    this.startLoading(project);

    const query = { ...this.widget.query };

    if (this.widget.query.filters) {
      query.filters = { ...this.widget.query.filters };
    }

    if (!query.filters) {
      query.filters = {};
    }

    if (!query.limit) {
      query.limit = 5000;
    }

    if (this.filters) {
      this.filters.forEach((filter: any) => {
        if (filter.enable) {
          query.filters[filter.name] = {
            options: filter.options,
            type: filter.type.toLowerCase()
          };
        }
      });
    }

    let projectObj = null;
    projectObj = this.projects.find(_p => project && (_p.id == project.id || _p.id == project));

    if (projectObj) {
      query.database = projectObj.database;
    }

    if ((query.longitude && !query.latitude) || (query.latitude && !query.longitude)) {
      this.stopLoading(project);
      return false;
    }
    const resHandler = (res: any[]) => {
      this.error = "";
      if (project && this.widget.databaseSeries) {
        if (projectObj && projectObj.name) {
          res.forEach(d => d.s = projectObj.name.slice(0, 20));
        }
      }

      this.dataByProject[project] = res;

      if (!this.tenantId && this.widget.projects && this.widget.projects.length > 1) {
        this.widgetData = this.mergeData(res);
      } else {
        this.widget.data = res;
      }
      this.parseMetrics();

      this.stopLoading(project);

      if (this.loading.length == 0) {
        this.init();
      }
    };

    const errHandler = (err: { status: number; statusText: string; error: any; }) => {
      if (err.status == 408) {
        this.error = err.statusText;
      } else if (err.status == 400) {
        const errorMessage = typeof err.error === 'string' ? err.error : JSON.stringify(err.error);

        if (errorMessage.includes("operation exceeded time limit")) {
          this.error = "Operation exceeded time limit";
        }
      } else if (err.status == 500) {
        this.error = "Server error";
      } else {
        this.error = "";
      }
      this.stopLoading(project);
    };

    if (this.project) {
      query.key = this.project.accessKey;
      query.project = this.project.id;
    }

    query.id = this.id;
    query.collection = this.widget.category;

    this.query.emit(query);
    this.runQuery(project, this.aggregate(query, this.projectLabel(projectObj)), { next: resHandler, error: errHandler });

    return true;
  }

  // Several projects in a project dashboard: one query each, its rows and forecast named after its project
  // (the series name the chart gives them).
  protected projectLabel(project: any): string | undefined {
    return (this.widget.projects?.length ?? 0) > 1 && project?.name ? project.name.slice(0, 20) : undefined;
  }

  // Every data query goes through here (ChartComponent overrides sendQuery but calls it too). `label`: the project
  // of one query among several, whose forecast meta adds to the others'.
  protected aggregate(query: any, label?: string): Observable<any[]> {
    return this.request(query).pipe(map(result => this.unwrap(result, label)));
  }

  // The raw answer to a query (rows, or { rows, meta, sources }), in the viewer's time zone, on the tenant when there
  // is one: what aggregate unwraps, and what a widget's second query (a block's landing) reads itself.
  protected request(query: any): Observable<any> {
    // Hours, days and date filters (today, 08:00 to 17:00) in the viewer's time zone, not in UTC.
    query = { ...query, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };

    if (!this.tenantId)
      return this.chartService.aggregate(query);

    // A widget saved before the option: projects compared, except for the types that show a total.
    const type = this.widget.type ?? '';
    const byProject = PROJECT_ROWS_WIDGET_TYPES.includes(type) || (query.byProject ?? BY_PROJECT_WIDGET_TYPES.includes(type));

    return this.chartService.aggregateTenant(this.tenantId, this.tenantQuery({ ...query, byProject })).pipe(
      map(result => {
        this.failedSources = result.sources?.failed ?? [];
        return result;
      })
    );
  }

  // A tenant query, without the projects removed from the page (untracked: may run inside the refresh effect).
  protected tenantQuery(query: any): any {
    const excluded = untracked(() => this.excludeProjects?.() ?? []);
    const zoned = { ...query, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    return excluded.length ? { ...zoned, excludeProjects: excluded } : zoned;
  }

  // Rows as they are, or { rows, meta } (tenant route, or any route with a forecast).
  private unwrap(result: any, label?: string): any[] {
    const forecast: ForecastMeta | null = Array.isArray(result) ? null : result?.meta?.forecast ?? null;
    if (label === undefined) this.forecastMeta = forecast;
    else if (forecast) this.forecastMeta = { ...forecast, series: [...(this.forecastMeta?.series ?? []), ...forecast.series.map(series => ({ ...series, p: series.p ?? label }))] };
    this.previousMeta = Array.isArray(result) ? null : result?.meta?.previous ?? null;
    return Array.isArray(result) ? result : result?.rows ?? [];
  }

  // The content of a widget (its 3D model, zones, planning) is not in the dashboard batch, too heavy:
  // fetched once, then `then` runs (right away when it is there: the editor sets it).
  private content$?: Observable<any>;

  protected withContent(then: () => void): void {
    if (this.widget?.content || !this.widget?.id) return then();

    this.content$ ??= this.chartService.getChartContent(this.widget.id).pipe(
      catchError(() => of({})),
      shareReplay(1)
    );
    this.content$.pipe(takeUntil(this.destroy$)).subscribe(content => {
      this.widget.content = content;
      then();
    });
  }

  // An icon or a description in the title settings: the shared rich header (app-widget-title).
  get richTitle(): boolean {
    return hasRichTitle(this.widget?.design?.title);
  }

  // Validation des options du graphique
  validationGraphOption(): boolean {
    if (this.widget.graphOption && Array.isArray(this.widget.graphOption)) {
      const missingRequiredOptions = this.widget.graphOption.filter((option: any) => {

        if (!option.required) return false;

        const query = this.widget.query;

        switch (option.input) {
          case 'field':
            return !query.field || Object.keys(query.field).length === 0;
          case 'aggregation':
            return !query.aggregation || Object.keys(query.aggregation).length === 0;
          case 'series':
            return !query.series;
          default:
            return false;
        }
      });

      if (missingRequiredOptions.length > 0) {
        this.error = `Configuration incomplète : ${missingRequiredOptions.map((opt: any) => opt.name).join(', ')}`;
        this.widgetData = null;
        this.widget.data = [];
        this.dataByProject = {};
        return false;
      }
    }

    return true;
  }

  // Fusion des données de plusieurs projets
  mergeData(data: any[]): any[] {
    if (!this.widget.data) this.widget.data = [];

    if (this.widget.databaseSeries) {
      this.widget.data = this.widget.data.concat(data);
    } else {
      let fieldType = "string";
      let fieldAgg = "distinct";

      if (this.widget.query?.field && Object.keys(this.widget.query.field).length) {
        const key = Object.keys(this.widget.query.field)[0];

        if (key) {
          fieldType = this.widget.query.field[key]?.type ?? "string";
        }

        const aggKey = Object.keys(this.widget.query.aggregation ?? {})[0];

        if (aggKey && this.widget.query.aggregation?.[aggKey]) {
          fieldAgg = this.widget.query.aggregation[aggKey]?.options?.aggregate ?? "distinct";
        }
      }

      const group: { [key: string]: any[] } = {};

      Object.keys(this.dataByProject).forEach(p => {
        const data = this.dataByProject[p];

        if (data) {
          data.forEach((d: any) => {
            let x;

            if (fieldType == "date") {
              if (d.x.week) {
                x = formatISO(setISOWeek(new Date(d.x.year, 0, 4), d.x.week));
              } else {
                x = formatISO(typeof d.x === 'string' ? parseISO(d.x) : new Date(d.x));
              }
            } else {
              x = d.x;
            }

            if (group[x]) {
              group[x]!.push(d);
            } else {
              group[x] = [d];
            }
          });
        }
      });

      const newData: { x: any; y: number | never[]; }[] = [];

      Object.keys(group).forEach(x => {
        if (group[x] && group[x].length > 0) {
          const y = this.reduceNumber(fieldAgg, group[x].map(d => d.y));
          newData.push({ x: group[x][0]?.x, y });
        }
      });

      this.widget.data = newData;
    }

    return this.widget.data;
  }

  reduceNumber(method: string, values: any[]): number | never[] {
    let analysedValue = 0;

    if (!values || !values.length) return [];

    switch (method) {
      case "mean":
        analysedValue = values.reduce((c, x) => c + x) / values.length;
        break;
      case "max":
        analysedValue = Math.max(...values);
        break;
      case "min":
        analysedValue = Math.min(...values);
        break;
      case "sum":
      case "count":
        analysedValue = values.reduce((c, x) => c + x);
        break;
      default:
        analysedValue = values[0];
        break;
    }

    return analysedValue;
  }

  // Export canvas
  async getCanvas(): Promise<HTMLCanvasElement | null> {
    try {
      // At least 2x: sharp once scaled on the PDF page.
      const canvas = await domToCanvas(this.htmlContent.nativeElement, {
        scale: Math.max(2, window.devicePixelRatio || 1)
      });

      this.onExport.emit(canvas);
      return canvas;
    } catch (e) {
      console.error(e);
      // Answered anyway: a PDF export waits for every widget.
      this.onExport.emit(null);
      return null;
    }
  }

  // Export en JPEG
  export(): void {
    this.getCanvas().then(canvas => {
      if (canvas) {
        this.download(canvas);
      }
    });
  }

  // Téléchargement
  download(canvas?: HTMLCanvasElement): void {
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = canvas?.toDataURL() ?? '';
    a.download = `${this.title}.jpeg`;
    a.click();
  }


  // Méthode abstraite à implémenter par les classes enfants
  abstract init(): void;

  parseMetrics() {
    if (this.hasPayloadTitle())
      this.setDisplayNames();
  }

  setDisplayNames() {
    this.widget.data.forEach((row: any) => {
      Object.keys(row).forEach(key => {
        if (typeof key == "string" && !isDate(key))
          row[key] = this.galleryTool.getDisplayName(row[key]);
      })
    });
  }

  hasPayloadTitle() {
    if (!this.widget.query) return false;

    let keys = Object.keys(this.widget.query);
    let payloadTitle = "payload.title";

    if (keys.includes(payloadTitle))
      return true;

    for (let key of keys) {
      let queryField = this.widget.query[key];

      if (typeof queryField != "object" || Array.isArray(queryField))
        continue;

      let subKeys = Object.keys(queryField);

      if (subKeys.includes(payloadTitle))
        return true;
    }

    return false;
  }
}
